import api from './axios'
import { saveBlob } from '../../lib/download'
import { Reseller } from '@crm-src/types/lead'

/** Reseller-of-reseller + the billing identity (spec A §3.1–3.2), shared by
 * create and update.
 *
 * `parent_reseller_id` is `string` and NOT optional on purpose: the form sends
 * `""` to DETACH a sub-reseller back to top level, and an optional field left
 * `undefined` would be omitted from the JSON, leaving the existing parent
 * standing. "Cleared" and "not mentioned" have to be different values on the
 * wire. */
export interface ResellerIdentityFields {
  parent_reseller_id?: string
  legal_name?: string
  tax_id?: string
  currency?: string
  payment_terms?: string
  invoice_footer?: string
  billing_email?: string
  billing_phone?: string
}

export interface CreateResellerData extends ResellerIdentityFields {
  name: string
  code?: string
  website?: string
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  assigned_to_id?: string
  assigned_salespeople_ids?: string[]
}

export interface UpdateResellerData extends ResellerIdentityFields {
  name: string
  code?: string
  website?: string
  address?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  assigned_to_id?: string
  assigned_salespeople_ids?: string[]
}

/**
 * Request params for the PAGINATED reseller list — the Resellers admin page,
 * and nothing else.
 *
 * The facet keys must match `Api::V1::ResellersController`'s `filterable`
 * declaration; `config-parity.test.ts` and crm-api's
 * `declared_keys_export_test.rb` are the two halves that keep them honest.
 */
export interface ResellerListFilters {
  page?: number
  per_page?: number
  /** One of `RESELLERS_TABLE.columns[].key`. See `FilesListParams.sort`. */
  sort?: string
  dir?: 'asc' | 'desc'
  search?: string
  /** Facet: a comma-separated list of country names. */
  country?: string
  /** Facet: a comma-separated list of user ids. */
  salesperson_id?: string
}

/** The paginated envelope, which this endpoint returns ONLY when `page` is sent. */
export interface ResellerPage {
  data: Reseller[]
  meta: { pagination: { current_page: number; per_page: number; total_count: number } }
}

/** One entry in the reseller lifecycle timeline. `kind` is a stable machine
 * key the UI maps to an icon and a colour; `title` is already human-readable,
 * because the sentence depends on data the server has and the client does not
 * (a decision note, a member's role, a quote number). */
export interface ResellerTimelineEntry {
  kind: string
  at: string
  title: string
  detail?: string | null
  actor?: string | null
}

export interface ResellerTimelinePage {
  entries: ResellerTimelineEntry[]
  pagination: { page: number; per_page: number; total_count: number; total_pages: number }
}

/** A row in the Customers or Prospects column of the Network tab. */
export interface ResellerNetworkRow {
  id: string
  full_name?: string | null
  email?: string | null
  phone?: string | null
  company_name?: string | null
  type: string
  created_at: string
}

export interface ResellerNetworkParams {
  kind?: 'customer' | 'prospect'
  search?: string
  page?: number
  per_page?: number
}

export interface ResellerNetworkPage {
  customers: ResellerNetworkRow[]
  pagination: { page: number; per_page: number; total_count: number; total_pages: number }
}

class ResellersApiService {
  /**
   * The BARE, unpaginated list: `{ resellers, total }`.
   *
   * Deliberately sends NO `page`, and that is the whole contract. crm-api's
   * `Api::V1::ResellersController#index` answers the paginated envelope only
   * when `page` is present and this legacy shape otherwise, because several
   * surfaces read `response.resellers` directly — the `useResellers` hook
   * (behind `reseller-selector`, the Customers and Prospects reseller facet
   * options and the prospect detail page) and `bulk-assign-reseller-modal`,
   * which calls this method itself. None of them paginates, and they do not
   * guard a missing key: switching the shape here would leave `resellers`
   * undefined, which empties some of them silently and CRASHES the Customers
   * page (`resellers.map` on every render).
   *
   * Use `getPage` for a list that sorts, filters or paginates.
   */
  async getAll(): Promise<{resellers: Reseller[], total: number}> {
    const response = await api.get('/resellers')
    return response.data
  }

  /**
   * One PAGE of resellers, sorted and filtered server-side — the envelope every
   * other CRM list uses. Sending `page` is what opts in to it (see `getAll`),
   * so `buildTableParams`' always-present `page` is load-bearing here.
   */
  async getPage(params: ResellerListFilters): Promise<ResellerPage> {
    const response = await api.get('/resellers', { params })
    return response.data
  }

  async getById(id: string): Promise<Reseller> {
    const response = await api.get(`/resellers/${id}`)
    return response.data
  }

  async create(data: CreateResellerData): Promise<Reseller> {
    const response = await api.post('/resellers', { reseller: data })
    return response.data
  }

  async update(id: string, data: UpdateResellerData): Promise<Reseller> {
    const response = await api.patch(`/resellers/${id}`, { reseller: data })
    return response.data
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/resellers/${id}`)
  }

  /**
   * Throw the COMPANY account switch (spec §4.3).
   *
   * What this actually changes is one column. What that column MEANS is
   * enforced in crm-api's `ResellerMembership.active_for` and nowhere else:
   * flipping it to `false` denies the reseller portal's sign-in AND kills every
   * live session on its next request. There is no separate "sign everyone out"
   * call to make afterwards.
   */
  async setActivation(id: string, active: boolean, reason?: string): Promise<Reseller> {
    const response = await api.patch(`/resellers/${id}/activation`, {
      reseller: { active, ...(reason ? { reason } : {}) },
    })
    return response.data.reseller
  }

  /** One page of the reseller lifecycle timeline (spec §5.2). */
  async getTimeline(id: string, params: { page?: number; per_page?: number } = {}): Promise<ResellerTimelinePage> {
    const response = await api.get(`/resellers/${id}/timeline`, { params })
    return response.data
  }

  /**
   * One page of the reseller's customers OR prospects.
   *
   * `kind` splits them IN SQL. Before this the endpoint returned every row and
   * the page filtered on `type === 'Lead'` in the browser — so a reseller with
   * 400 customers shipped 400 rows to render 10, twice, once per column.
   *
   * The response key is `customers` for both kinds; see the controller for why
   * it was not renamed.
   */
  async getNetwork(id: string, params: ResellerNetworkParams): Promise<ResellerNetworkPage> {
    const response = await api.get(`/resellers/${id}/customers`, { params })
    return response.data
  }

  async bulkDelete(resellerIds: string[]): Promise<{ 
    message: string, 
    deleted_count: number, 
    failed_deletions?: Array<{
      id: string,
      name: string,
      error: string
    }>
  }> {
    const response = await api.delete('/resellers/bulk_delete', {
      data: { reseller_ids: resellerIds }
    })
    return response.data
  }

  /** Attach one of OUR salespeople (a CRM user) to this reseller. crm-api
   * refuses anyone who cannot see every record (`should_see_all_customers?`) —
   * assigning other salespeople is an admin act. Returns the refreshed reseller. */
  async assignSalesperson(resellerId: string, userId: string): Promise<Reseller> {
    const response = await api.post(`/resellers/${resellerId}/assign_salesperson`, {
      user_id: userId,
      role: 'salesperson',
    })
    return response.data.reseller
  }

  /** Detach a salesperson. The assignment is DEACTIVATED, not deleted, so the
   * history of who looked after the reseller survives. Same admin gate. */
  async removeSalesperson(resellerId: string, userId: string): Promise<Reseller> {
    const response = await api.delete(`/resellers/${resellerId}/remove_salesperson`, {
      data: { user_id: userId },
    })
    return response.data.reseller
  }

  async bulkAssignSalespeople(resellerIds: string[], salespeopleIds: string[]): Promise<{
    message: string,
    assigned_resellers_count: number,
    assigned_customers_count: number,
    assigned_prospects_count: number,
    total_entities: number,
    total_assignments: number,
    failed_assignments?: Array<{
      id: string,
      name: string,
      error: string
    }>
  }> {
    const response = await api.post('/resellers/bulk_assign_salespeople', {
      reseller_ids: resellerIds,
      salesperson_ids: salespeopleIds
    })
    return response.data
  }

  async import(resellers: any[]): Promise<any> {
    const response = await api.post('/resellers/import', { resellers })
    return response.data
  }

  async export(params?: any): Promise<any> {
    const response = await api.get('/resellers/export', { params })
    return response.data
  }

  async downloadExport(format: 'csv' | 'xlsx' = 'xlsx', filters?: any, filename?: string): Promise<void> {
    const params = new URLSearchParams()

    if (filters) {
      const exportFilters = { ...filters }
      delete exportFilters.page
      delete exportFilters.per_page

      Object.entries(exportFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value))
        }
      })
    }

    params.append('format', format)

    const response = await api.get(`/resellers/export?${params.toString()}`, {
      responseType: 'blob'
    })

    const blob = response.data
    const defaultFilename = `resellers_export_${new Date().toISOString().split('T')[0]}.${format}`

    // Native OS Save-As dialog in the desktop app; browser download on the web.
    await saveBlob(filename || defaultFilename, blob)
  }
}

export const resellersApiService = new ResellersApiService()
export default resellersApiService


